import React, { useEffect, useState } from "react";
import { useParams } from "react-router";
import { CONFIG } from "../../../constants";
import SimpleLayout from "../../../layouts/SimpleLayout";
import {
  LogoIcon,
  LogoTextIcon,
  PhoneIcon,
  WhatsAppIcon,
} from "../../../components/icons";
import { fromNow } from "../../../utils/helpers";
import { UnconfirmedLeadsService } from "../../../services/unconfirmed-leads.service";
import { BrandingModel, UnconfirmedLeadModel } from "../../../utils/types";
import { ToastService } from "../../../services";
import LinkExpired from "./link-expired";

const NotificationsCard = ({
  leadsData,
  branding,
}: {
  leadsData: UnconfirmedLeadModel;
  branding: BrandingModel;
}) => {
  return (
    <div className="pl-2 pr-4">
      <div className="flex justify-between tablet-landscape:items-center rounded-2xl shadow-md bg-white p-5 mb-5 tablet-landscape:flex-row flex-col">
        <div>
          <div className="text-2xl font-bold">Leads not responded to</div>
          <div className="text-xs">Don't miss a chance to connect.</div>
        </div>
        <div className="flex space-x-2 pt-5 tablet-landscape:w-1/2 ">
          <div className="flex flex-col w-full tablet-landscape:w-1/2 items-center tablet-landscape:ml-auto">
            <div className="w-full rounded-md py-2" style={{ backgroundColor: branding.button_bg_color + "22" }}>
              <div className="text-xl font-bold text-center" style={{ color: branding.button_bg_color }}>
                {leadsData?.unconfirmed_details?.count}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const LeadCards = ({ leadsData, branding }: { leadsData: UnconfirmedLeadModel; branding: BrandingModel }) => {
  function cleanAndTruncate(input: string, length: number = 60): string {
    const cleaned = input.replace(/\s+/g, " ").trim();

    return cleaned.slice(0, length) + "...";
  }
  return (
    <>
      {leadsData?.chat_intro_logs.map((lead) => (
        <>
          <div
            key={lead.reference_id}
            className="flex flex-col justify-between rounded-2xl shadow-md bg-white p-5"
          >
            <div className="flex flex-row items-center justify-between">
              <div className="text-xl font-bold text-blue">Lead Details</div>

              <div className="bg-gray-c4 bg-opacity-20 text-2xs text-gray-400 py-2 px-4 rounded-lg">
                {fromNow(lead.modified)}
              </div>
            </div>

            <div className="bg-blue bg-opacity-8 w-full space-y-2 p-2 rounded-md mt-2">
              <div className="flex flex-row items-center space-x-2">
                <div className="text-sm font-bold w-18">Name:</div>
                <div className="text-sm">{lead.a_party_name || "New Lead"}</div>
              </div>
              <div className="flex flex-row items-center space-x-2">
                <div className="text-sm font-bold w-18">Date:</div>
                <div className="text-sm">
                  {new Date(lead.modified).toLocaleTimeString("en-US", {
                    hour: "2-digit",
                    minute: "2-digit",
                    hour12: false,
                  })}
                  <span>, </span>
                  {new Date(lead.modified).toLocaleDateString("en-GB", {
                    day: "2-digit",
                    month: "short",
                    year: "numeric",
                  })}
                </div>
              </div>
              {lead.message && (
                <div className="flex flex-row items-center space-x-2">
                  <div className="text-sm font-bold w-18">Message:</div>
                  <div className="text-sm">
                    {cleanAndTruncate(lead.message)}
                  </div>
                </div>
              )}
            </div>
            <div className="text-md font-semibold py-2">Property</div>
            <div className="flex flex-row  space-x-2 py-2">
              {lead.image_url ? (
                <div className="overflow-hidden flex items-center justify-center w-[150px] tablet-landscape:w-[120px] h-[100px]">
                  <img
                    className="w-full h-full object-cover rounded-2xl"
                    alt={lead.image_url}
                    src={lead.image_url}
                  />
                </div>
              ) : (
                <div className="flex items-center justify-center bg-gray-fa rounded-2xl w-[150px] tablet-landscape:w-[120px] h-[100px]">
                  <LogoIcon color={["#E4E4E4", "#FAFAFA"]} size={50} />
                </div>
              )}
              <div className=" w-full flex flex-col space-y-1">
                <div className="text-sm font-bold">
                  {" "}
                  {Number(lead.price).toLocaleString()}
                </div>

                <div className="w-full flex">
                  <div className="flex flex-col space-y-1">
                    <div className="text-sm">{lead.property_name}</div>
                    <div className="text-sm">{lead.community}</div>
                    <div className="text-sm">
                      <span>Ref ID: </span> {lead.reference_id}
                    </div>
                  </div>
                  <div className="flex-row space-x-1 ml-auto hidden tablet-landscape:flex">
                    <div
                      className="flex w-full items-center space-x-1 rounded-xl px-10 font-bold py-4 cursor-pointer"
                      style={{ backgroundColor: branding.button_bg_color, color: branding.button_text_color }}
                      onClick={() =>
                        window.open(
                          `${CONFIG.WHATSAPP_RPLY_LINK}/t/${lead.short_code}`,
                          "_blank"
                        )
                      }
                    >
                      <PhoneIcon size={15} color={branding.button_text_color} />
                      <span className="text-xs">Call Now</span>
                    </div>
                    <div
                      className="flex w-full items-center justify-center space-x-1 rounded-xl px-10 font-bold py-4 cursor-pointer"
                      style={{ backgroundColor: branding.button_bg_color, color: branding.button_text_color }}
                      onClick={() =>
                        window.open(
                          `${CONFIG.WHATSAPP_RPLY_LINK}/l/${lead.short_code}`,
                          "_blank"
                        )
                      }
                    >
                      <WhatsAppIcon size={15} color={branding.button_text_color} />
                      <span className="text-xs">WhatsApp</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="flex flex-row space-x-1 pt-2 tablet-landscape:hidden">
              <div
                className="flex w-full items-center justify-center space-x-1 rounded-xl font-bold py-4 cursor-pointer"
                style={{ backgroundColor: branding.button_bg_color, color: branding.button_text_color }}
                onClick={() =>
                  window.open(
                    `${CONFIG.WHATSAPP_RPLY_LINK}/t/${lead.short_code}`,
                    "_blank"
                  )
                }
              >
                <PhoneIcon size={15} color={branding.button_text_color} />
                <span>Call Now</span>
              </div>
              <div
                className="flex w-full items-center justify-center space-x-1 rounded-xl font-bold py-4 cursor-pointer"
                style={{ backgroundColor: branding.button_bg_color, color: branding.button_text_color }}
                onClick={() =>
                  window.open(
                    `${CONFIG.WHATSAPP_RPLY_LINK}/l/${lead.short_code}`,
                    "_blank"
                  )
                }
              >
                <WhatsAppIcon size={15} color={branding.button_text_color} />
                <span>WhatsApp</span>
              </div>
            </div>
          </div>
        </>
      ))}
    </>
  );
};

const BRANDING_CACHE_KEY = (uuid: string) => `unconfirmed_leads_branding_${uuid}`;

const UnconfirmedLeads = () => {
  const { uuid } = useParams<{ uuid: string }>();
  const [leadsData, setLeadsData] = useState<UnconfirmedLeadModel | null>(null);
  const [isExpired, setIsExpired] = useState(false);
  const [branding, setBranding] = useState<BrandingModel | null>(() => {
    if (!uuid) return null;
    try {
      const cached = localStorage.getItem(BRANDING_CACHE_KEY(uuid));
      return cached ? JSON.parse(cached) : null;
    } catch {
      return null;
    }
  });

  useEffect(() => {
    if (!uuid) return;
    UnconfirmedLeadsService.getUnconfirmedLeads(uuid)
      .then((res) => {
        setLeadsData(res);
        setBranding(res.branding);
        try {
          localStorage.setItem(BRANDING_CACHE_KEY(uuid), JSON.stringify(res.branding));
        } catch { /* storage quota exceeded — ignore */ }
      })
      .catch((error) => {
        console.error("Failed to fetch unconfirmed leads:", error);
        if (error.response?.status === 416) {
          const expiredBranding = error.response?.data?.branding ?? null;
          setBranding(expiredBranding);
          setIsExpired(true);
        } else {
          ToastService.showHttpError(error, "Failed to fetch unconfirmed leads");
        }
      });
  }, [uuid]);

  if (!uuid) {
    return <div>Invalid lead ID</div>;
  }

  if (isExpired) {
    return <LinkExpired branding={branding} />;
  }

  const activeBranding = branding ?? leadsData?.branding;

  return (
    <SimpleLayout blank={true}>
      {activeBranding && (
        <div
          className="w-full flex items-center px-6 py-3 space-x-3"
          style={{ backgroundColor: activeBranding.navbar_bg_color }}
        >
          {activeBranding.logo_url && (
            <img src={activeBranding.logo_url} alt={activeBranding.company_name} className="h-8 object-contain" />
          )}
          {activeBranding.show_company_name && (
            <span className="text-white font-bold text-sm">{activeBranding.company_name}</span>
          )}
        </div>
      )}
      <div className="w-full h-[calc(100vh-52px)] flex items-center justify-center">
        <div className="tablet-landscape:min-w-200 min-w-100 min-h-full flex flex-col pt-5">
          {!leadsData || !activeBranding ? (
            <div className="flex justify-center items-center h-32"></div>
          ) : (
            <>
              <NotificationsCard leadsData={leadsData} branding={activeBranding} />
              <div className="flex flex-col space-y-3 h-[calc(100vh-277px)] tablet-landscape:h-[calc(100vh-257px)] overflow-y-auto px-2">
                <LeadCards leadsData={leadsData} branding={activeBranding} />
              </div>
            </>
          )}

          <div className="w-full mt-auto flex items-center justify-center py-3">
            <div className="text-sm text-blue-dark p-2">powered by</div>
            {activeBranding?.logo_url ? (
              <img src={activeBranding.logo_url} alt={activeBranding.company_name} className="h-5 object-contain" />
            ) : (
              <>
                <LogoIcon size={18} color={["blue-dark", "white"]} />
                <LogoTextIcon className="ml-1" size={70} color="blue-dark" />
              </>
            )}
          </div>
        </div>
      </div>
    </SimpleLayout>
  );
};

export default UnconfirmedLeads;
